import type React from "react"
import { useId, useState } from "react"

import MoreVertIcon from "@mui/icons-material/MoreVert"
import { IconButton, Menu } from "@mui/material"

interface MoreMenuProps {
  ariaLabel: string
  disabled?: boolean
  loading?: boolean
  moreMenuItems: React.ReactNode
}

/**
 * Overflow actions menu (more-vert IconButton + Menu).
 */
export const MoreMenu: React.FC<MoreMenuProps> = ({
  ariaLabel,
  disabled = false,
  loading = false,
  moreMenuItems,
}) => {
  const moreButtonId = useId()
  const moreMenuId = useId()
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(null)
  const isMenuOpen = Boolean(moreMenuAnchor)

  const closeMoreMenu = () => {
    setMoreMenuAnchor(null)
  }

  const handleMoreMenuButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setMoreMenuAnchor(event.currentTarget)
  }

  return (
    <>
      <IconButton
        aria-controls={isMenuOpen ? moreMenuId : undefined}
        aria-expanded={isMenuOpen ? "true" : undefined}
        aria-haspopup="true"
        aria-label={ariaLabel}
        color="neutral"
        disabled={disabled}
        id={moreButtonId}
        loading={loading}
        onClick={handleMoreMenuButtonClick}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={moreMenuAnchor}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        id={moreMenuId}
        onClick={closeMoreMenu}
        onClose={closeMoreMenu}
        open={isMenuOpen}
        slotProps={{
          list: {
            "aria-labelledby": moreButtonId,
          },
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
      >
        {moreMenuItems}
      </Menu>
    </>
  )
}
